import {useEffect, useMemo, useRef, useState} from 'react';
import {
   Chart as ChartJS,
   CategoryScale,
   LinearScale,
   PointElement,
   LineElement,
   Title,
   Tooltip as ChartTooltip,
   Legend,
   BarElement,
   ChartType
} from 'chart.js';
import {Box, Container, Grid, IconButton, Slider, Typography, Tooltip} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {Bar, Line} from 'react-chartjs-2';
import {Mark} from '@mui/material/Slider/useSlider.types';
import {axis} from '../../utils/types/multiAxisChart';

// Registrar los componentes necesarios
ChartJS.register(
   CategoryScale,
   LinearScale,
   PointElement,
   LineElement,
   BarElement,
   Title,
   ChartTooltip,
   Legend
);

interface dataChar {
   codigo: string;
   denominacion: string;
   mark?: string;
   axis?: axis[];
}
interface LineBarDateCharProps {
   title: string;
   data: dataChar[];
   categories: {[key: string]: {label: string; borderColor: string; backgroundColor: string}};
   defaultSlider?: {start: number; end: number};
}

export const LineBarMultiAxisChar: React.FC<LineBarDateCharProps> = ({
   title,
   data,
   categories,
   defaultSlider = {end: 4, start: 0}
}) => {
   /* Toggle */
   const chartBarRef = useRef(null);
   const [chartType, setChartType] = useState<ChartType>('bar');
   /* Slider */
   const [valueSlider, setValueSlider] = useState<[number, number]>([
      defaultSlider.start,
      defaultSlider.end
   ]);
   const [marks, setMarks] = useState<Mark[]>([]);
   const [columns, setColumns] = useState<string[]>([]);
   /* Data */
   const [dataViewChart, setDataViewChart] = useState<dataChar[]>([]);

   /* Gráficos */
   const handleSliderChange = (event: Event, newValue: number | number[]) => {
      const [mesInicial, mesFinal] = newValue as [number, number];
      setValueSlider([mesInicial, mesFinal]);
   };

   const chartData = useMemo(() => {
      let labels: string[] = dataViewChart.map(value => value.denominacion);

      const datasets: Record<string, number[]> = {};

      Object.keys(categories).forEach(category => {
         datasets[category] = [];
      });
      // Llenamos los datasets dinámicamente
      dataViewChart.forEach(item => {
         const axisMap = item.axis?.reduce((acc, estado) => {
            acc[estado.codigo] = estado.value;
            return acc;
         }, {} as Record<string, number>);

         // Añadir datos a los datasets segun las categorías
         Object.keys(categories).forEach(category => {
            datasets[category].push(axisMap?.[category] || 0);
         });
      });

      // Generar los datasets para el gráfico
      const datasetArray = Object.keys(categories).map(category => ({
         label: categories[category].label,
         data: datasets[category],
         borderColor: categories[category].borderColor,
         backgroundColor: categories[category].backgroundColor,
         tension: 0.1,
         borderWidth: 1
      }));

      return {
         labels,
         datasets: datasetArray
      };
   }, [dataViewChart]);

   const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
         tooltip: {
            backgroundColor: '#fff',
            titleColor: '#333',
            bodyColor: '#333',
            borderColor: '#ccc',
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: true,
            padding: 8,
            boxPadding: 6,
            boxWidth: 10,
            boxHeight: 10
         }
      },
      scales: {
         x: {
            ticks: {
               callback: (value: string | number, index: number) => {
                  const currColumn = dataViewChart[index];
                  const label = currColumn.denominacion || String(value);
                  return label.length > 11 ? label.slice(0, 11) + '…' : label;
               }
            }
         }
      }
   };

   useEffect(() => {
      if (data) {
         const newMarks: Mark[] = data.map((currRow, index) => ({
            value: index,
            label: currRow.mark || currRow.codigo
         }));
         const newColumns: string[] = data.map(currRow => currRow.denominacion);
         setMarks(newMarks);
         setColumns(newColumns);
         setDataViewChart(data);
      }
   }, [data]);

   useEffect(() => {
      if (data && data.length > 0) {
         const [start, end] = valueSlider;
         setDataViewChart(data.slice(start, end + 1));
      }
   }, [valueSlider]);

   return (
      <Container sx={{height: '100%', maxHeight: '80vh'}}>
         <Grid item container>
            <Grid item xs={12}>
               <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  {/* Iconos de tipo de gráfico (izquierda) */}
                  <Box display="flex" gap={1}>
                     <Tooltip title="Gráfico de Barras" placement="top" arrow>
                        <IconButton
                           onClick={() => setChartType('bar')}
                           sx={{
                              p: 0.5,
                              color: chartType === 'bar' ? 'primary.main' : 'text.disabled',
                              '&:hover': {
                                 backgroundColor: 'primary.light',
                                 color: 'primary.main'
                              }
                           }}
                        >
                           <BarChartIcon fontSize="large" />
                        </IconButton>
                     </Tooltip>
                     <Tooltip title="Gráfico Lineal" placement="top" arrow>
                        <IconButton
                           onClick={() => setChartType('line')}
                           sx={{
                              p: 0.5,
                              color: chartType === 'line' ? 'primary.main' : 'text.disabled',
                              '&:hover': {
                                 backgroundColor: 'primary.light',
                                 color: 'primary.main'
                              }
                           }}
                        >
                           <ShowChartIcon fontSize="large" />
                        </IconButton>
                     </Tooltip>
                  </Box>

                  {/* Título centrado */}
                  <Typography variant="h5" mt={0.5} textAlign="center" lineHeight={1.1}>
                     {title ?? ''}
                  </Typography>
                  <Box sx={{width: '94px'}}></Box>
               </Box>
            </Grid>
         </Grid>
         <Grid item sm={12} sx={{height: 'calc(100% - 100px)'}}>
            {chartType === 'line' ? (
               <Line options={options} data={chartData} />
            ) : (
               <Bar ref={chartBarRef} options={options} data={chartData} />
            )}
         </Grid>
         <Grid item sm={12}>
            <div style={{width: '90%', margin: 'auto'}}>
               <Slider
                  value={valueSlider}
                  onChange={handleSliderChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={columns.length - 1}
                  marks={marks}
                  step={1}
                  valueLabelFormat={index => columns[index]}
                  style={{fontSize: '5px important!'}}
               />
            </div>
         </Grid>
      </Container>
   );
};
